import { UseMutationResult, UseQueryOptions, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import { ASTNode, DocumentNode, print } from 'graphql'
import { SERVER_URL } from '../../apis'
import { getJwtToken } from '../../auth'
import { axiosPost } from '../../axiosHelper'

interface UseGraphQLQueryProps<TData, TVariables> {
  gqlQuery: DocumentNode
  queryKey: any[]
  enabled?: boolean
  variables?: TVariables
  options?: object
  refetchOnWindowFocus?: boolean
}

interface UseGraphQLMutationProps<TData, TVariables> {
  gqlMutation: ASTNode
  mutationKey: any[]
  enabled?: boolean
  variables?: TVariables
  options?: object
}

export function useGraphQLQuery<TData, TVariables = object>({
  gqlQuery,
  queryKey,
  enabled = true,
  variables,
  options,
  refetchOnWindowFocus = false,
}: UseGraphQLQueryProps<TData, TVariables> & {
  useQueryOptions?: Omit<UseQueryOptions<TData>, 'initialData'>
}): UseQueryResult<TData> {
  const fetchGraphQLData = async (): Promise<TData> => {
    const url = `${SERVER_URL}/graphql`
    const token = getJwtToken()

    try {
      const response = await axiosPost(
        url,
        {
          query: print(gqlQuery),
          variables,
        },
        { initialToken: token }
      )
      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message || 'GraphQL 요청 중에 오류가 발생했습니다.')
      }
      return response.data
    } catch (error) {
      throw new Error(error?.message || 'GraphQL 요청 중에 오류가 발생했습니다.')
    }
  }

  return useQuery<TData, unknown, TData>({
    queryKey,
    queryFn: fetchGraphQLData,
    enabled,
    ...options,
    refetchOnWindowFocus: refetchOnWindowFocus,
  })
}

export function useGraphQLMutation<TData, TVariables = object>({
  gqlMutation,
  mutationKey,
  variables,
  options,
}: UseGraphQLMutationProps<TData, TVariables>): UseMutationResult<TData> {
  const mutateGraphQLData = async (mutationVariables: TVariables): Promise<TData> => {
    const url = `${SERVER_URL}/graphql`
    const token = getJwtToken()

    try {
      const response = await axiosPost(
        url,
        {
          query: print(gqlMutation),
          variables: mutationVariables,
        },
        { initialToken: token }
      )

      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message || 'GraphQL 뮤테이션 중에 오류가 발생했습니다.')
      }

      return response.data
    } catch (error) {
      throw new Error(error.message || 'GraphQL 뮤테이션 중에 오류가 발생했습니다.')
    }
  }

  return useMutation<TData, Error, TVariables>({
    mutationKey,
    mutationFn: mutateGraphQLData,
    ...options,
  })
}
